import { SearchOutlined } from '@ant-design/icons';
import { WaterbodyDetail, Waypoint } from '@omniafishing/core';
import { Segmented } from 'antd';
import classNames from 'classnames';
import dayjs from 'dayjs';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { MapRef } from 'react-map-gl';
import { useUser } from '../../../../hooks/use_user';
import { useWaypoints } from '../../../../hooks/use_waypoints';
import { apiV1 } from '../../../../lib/api';
import { getImgixPath } from '../../../../lib/imgix';
import { CurrentConditionsTimestamp } from '../../../current_conditions/current_conditions_timestamp';
import { OmniaButton } from '../../../omnia_button/omnia_button';
import {
  createLabel,
  WaterbodySearchSelect,
} from '../../../waterbody_search_select/waterbody_search_select';
import { WaterbodySelectValue } from '../../omnia_map_search';
import { WaypointMarker } from '../../waypoint_marker';
import { ConditionsUpgrade } from './conditions_upgrade';
import styles from './waypoint_form.less';
import {
  debouncedGetWaterbodyIdAtPoint,
  decimalToDMS,
  DMStoDecimal,
  getLabel,
  queryRenderedFeaturesAtPoint,
  truncateTo6Decimal,
} from './waypoint_form_helper';

interface WaypointFormProps {
  editableWaypoint: Waypoint | null;
  mapRef: MapRef;
  newWaypoint: Pick<Waypoint, 'lat' | 'lng' | 'waypoint_type' | 'name'> | null;
  onEditableWaypointChange: (waypoint: Waypoint | null, flyTo?: boolean) => void;
  onNewWaypointChange: (wp: Pick<Waypoint, 'lat' | 'lng' | 'waypoint_type' | 'name'>) => void;
  onSelectedWaypointChange: (waypoint: Waypoint) => void;
}

const formFields = {
  lat: 'lat',
  lng: 'lng',
  lat_deg: 'lat_deg',
  lat_min: 'lat_min',
  lat_sec: 'lat_sec',
  lng_deg: 'lng_deg',
  lng_min: 'lng_min',
  lng_sec: 'lng_sec',
  name: 'name',
  waypoint_type: 'waypoint_type',
  comments: 'comments',
  waterbodyId: 'waterbodyId',
} as const;
interface FormValues {
  comments?: string;
  img?: string;
  lat: number;
  lng: number;
  name: string;
  waterbodyId?: string;
  waypoint_type: Waypoint['waypoint_type'];
}

export const waypointTypes: Waypoint['waypoint_type'][] = [
  'marker',
  'fish',
  'structure',
  'great_spot',
  'hazard',
];

const isValueEmpty = (value: string | number | null | undefined) => {
  return value === '' || value == null || value === '-' || value === '.' || value === 'e';
};

export const WaypointForm = (props: WaypointFormProps) => {
  const {
    editableWaypoint,
    mapRef,
    newWaypoint,
    onEditableWaypointChange,
    onNewWaypointChange,
    onSelectedWaypointChange,
  } = props;
  const [interactiveWaterbody, setInteractiveWaterbody] = useState<WaterbodyDetail | null>(null);
  const [coordinateType, setCoordinateType] = useState<'Decimal' | 'DMS'>('Decimal');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const nowTimestamp = useRef(new Date().toISOString());
  const { isPro } = useUser();
  const { createWaypoint, updateWaypoint } = useWaypoints();

  const waypointLat = editableWaypoint ? editableWaypoint.lat : newWaypoint.lat;
  const waypointLng = editableWaypoint ? editableWaypoint.lng : newWaypoint.lng;
  const waypointComments = editableWaypoint ? editableWaypoint.comments : '';
  const waypointName = editableWaypoint ? editableWaypoint.name : newWaypoint.name;
  const waypointWaterbodyId = editableWaypoint ? editableWaypoint?.waterbody?.id : null;
  const waypointType = editableWaypoint
    ? editableWaypoint.waypoint_type
    : newWaypoint.waypoint_type;

  const {
    degrees: lat_degrees,
    minutes: lat_minutes,
    seconds: lat_seconds,
  } = decimalToDMS(waypointLat);

  const {
    degrees: lng_degrees,
    minutes: lng_minutes,
    seconds: lng_seconds,
  } = decimalToDMS(waypointLng);

  const {
    formState: { errors },
    handleSubmit,
    register,
    setValue,
    setError,
    clearErrors,
    getValues,
    trigger,
    watch,
    control,
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      comments: waypointComments,
      img: null,
      lat: truncateTo6Decimal(waypointLat),
      lng: truncateTo6Decimal(waypointLng),
      lat_deg: lat_degrees,
      lat_min: lat_minutes,
      lat_sec: lat_seconds,
      lng_deg: lng_degrees,
      lng_min: lng_minutes,
      lng_sec: lng_seconds,
      name: waypointName,
      waterbodyId: waypointWaterbodyId,
      waypoint_type: waypointType,
    },
  });

  useEffect(() => {
    if (editableWaypoint?.waterbody) {
      setInteractiveWaterbody(editableWaypoint.waterbody as WaterbodyDetail);
    }
  }, [editableWaypoint?.waterbody]);

  const watchedFields = watch();

  const handleLatChange = useCallback(
    (e) => {
      const value = e.target.value;
      const newLat = parseFloat(value);

      if (value === '' || value == null) {
        setError(formFields.lat, {
          type: 'manual',
          message: 'A valid Latitude is required.',
        });
        return;
      }
      if (newLat > 90 || newLat < -90) {
        setError(formFields.lat, {
          type: 'manual',
          message: 'Latitude must be between -90 and 90 degrees.',
        });
      } else {
        clearErrors(formFields.lat);
        if (newWaypoint) {
          onNewWaypointChange({ ...newWaypoint, lat: newLat });
        } else if (editableWaypoint) {
          onEditableWaypointChange({ ...editableWaypoint, lat: newLat });
        }
      }
    },
    [newWaypoint, editableWaypoint]
  );

  const handleLngChange = useCallback(
    (e) => {
      const value = e.target.value;
      const newLng = Number(value);

      if (value === '' || value == null) {
        setError(formFields.lng, {
          type: 'manual',
          message: 'Longitude is required.',
        });
        return;
      }
      if (newLng > 180 || newLng < -180) {
        setError(formFields.lng, {
          type: 'manual',
          message: 'Longitude must be between -180 and 180 degrees.',
        });
      } else {
        clearErrors(formFields.lng);
        if (newWaypoint) {
          onNewWaypointChange({ ...newWaypoint, lng: newLng });
        } else if (editableWaypoint) {
          onEditableWaypointChange({ ...editableWaypoint, lng: newLng });
        }
      }
    },
    [newWaypoint, editableWaypoint]
  );

  const handleLatDMSChange = useCallback(
    (field: keyof typeof formFields, value) => {
      if (isValueEmpty(value)) {
        setError(formFields.lat_deg, {
          type: 'manual',
          message: 'A valid value is required for all Latitude fields.',
        });
        return;
      }

      if ((field === formFields.lat_deg || field === formFields.lat_min) && value.includes('.')) {
        setValue(field, watchedFields[field]);
        return;
      }
      const parsedValue = parseFloat(value);
      const numbersOnlyValue = value.replace(/[^0-9]/g, '');
      const { lat_deg, lat_min, lat_sec } = getValues();

      if (
        field === formFields.lat_deg &&
        (parsedValue > 90 || parsedValue < -90 || numbersOnlyValue.length > 2)
      ) {
        setValue(field, watchedFields[field]);
        return;
      } else if (
        field === formFields.lat_min &&
        (parsedValue > 59 || parsedValue < 0 || numbersOnlyValue.length > 2)
      ) {
        setValue(field, watchedFields[field]);
        return;
      } else if (
        field === formFields.lat_sec &&
        (parsedValue > 59.999 || parsedValue < 0 || numbersOnlyValue.length > 4)
      ) {
        setValue(field, watchedFields[field]);
        return;
      }

      const updates = { ...{ lat_deg, lat_min, lat_sec }, [field]: parsedValue };

      // if one of the fields is empty and user tries to change another value, lock to prev values
      if (
        isValueEmpty(updates.lat_deg) ||
        isValueEmpty(updates.lat_min) ||
        isValueEmpty(updates.lat_sec)
      ) {
        const prevValue = watchedFields[field];
        setValue(field, prevValue);
        return;
      }
      const decimalLat = DMStoDecimal(updates.lat_deg, updates.lat_min, updates.lat_sec);

      if (isNaN(decimalLat)) {
        setError(formFields.lat_deg, {
          type: 'manual',
          message: 'Latitude must be a number.',
        });
        return;
      }

      setValue(formFields.lat, decimalLat); // set decimal lat to keep them in sync
      clearErrors([formFields.lat_deg, formFields.lat_min, formFields.lat_sec]);

      if (newWaypoint) {
        onNewWaypointChange({ ...newWaypoint, lat: decimalLat });
      } else if (editableWaypoint) {
        onEditableWaypointChange({ ...editableWaypoint, lat: decimalLat });
      }
    },
    [newWaypoint, editableWaypoint, DMStoDecimal, setValue, clearErrors, getValues, watchedFields]
  );

  const handleLngDMSChange = useCallback(
    (field: keyof typeof formFields, value) => {
      if (value === '' || value == null || value === '-' || value === '.' || value === 'e') {
        setError(formFields.lng_deg, {
          type: 'manual',
          message: 'A valid value is required for all Longitude fields.',
        });
        return;
      }

      if ((field === formFields.lng_deg || field === formFields.lng_min) && value.includes('.')) {
        setValue(field, watchedFields[field]);
        return;
      }

      const parsedValue = parseFloat(value);
      const numbersOnlyValue: string = value.replace(/[^0-9]/g, '');
      const { lng_deg, lng_min, lng_sec } = getValues();

      if (
        field === formFields.lng_deg &&
        (parsedValue > 180 || parsedValue < -180 || numbersOnlyValue.length > 2)
      ) {
        setValue(field, watchedFields[field]);
        return;
      } else if (
        field === formFields.lng_min &&
        (parsedValue > 59 || parsedValue < 0 || numbersOnlyValue.length > 2)
      ) {
        setValue(field, watchedFields[field]);
        return;
      } else if (
        field === formFields.lng_sec &&
        (parsedValue > 59.99 || parsedValue < 0 || numbersOnlyValue.length > 4)
      ) {
        setValue(field, watchedFields[field]);
        return;
      }

      const updates = {
        ...{
          lng_deg,
          lng_min,
          lng_sec,
        },
        [field]: parsedValue,
      };

      // if one of the fields is empty and user tries to change another value, lock values
      if (!updates.lng_deg || !updates.lng_min || !updates.lng_sec) {
        const prevValue = watchedFields[field];
        setValue(field, prevValue);
        return;
      }

      const decimalLng = DMStoDecimal(updates.lng_deg, updates.lng_min, updates.lng_sec);

      if (isNaN(decimalLng)) {
        setError(formFields.lng_deg, {
          type: 'manual',
          message: 'Longitude must be a number.',
        });
        return;
      }

      setValue(formFields.lng, decimalLng); // set decimal lng to keep them in sync
      clearErrors([formFields.lng_deg, formFields.lng_min, formFields.lng_sec]);

      if (newWaypoint) {
        onNewWaypointChange({ ...newWaypoint, lng: decimalLng });
      } else if (editableWaypoint) {
        onEditableWaypointChange({ ...editableWaypoint, lng: decimalLng });
      }
    },
    [newWaypoint, editableWaypoint, DMStoDecimal, setValue, clearErrors, getValues, watchedFields]
  );

  // keep track of lat/lng changes from map marker drag changes
  useEffect(() => {
    setValue(formFields.lat, truncateTo6Decimal(waypointLat));
    setValue(formFields.lng, truncateTo6Decimal(waypointLng));
    setValue(formFields.lat_deg, lat_degrees);
    setValue(formFields.lat_min, lat_minutes);
    setValue(formFields.lat_sec, lat_seconds);
    setValue(formFields.lng_deg, lng_degrees);
    setValue(formFields.lng_min, lng_minutes);
    setValue(formFields.lng_sec, lng_seconds);
    clearErrors([formFields.lng_deg, formFields.lat_deg, formFields.lat, formFields.lng]);
  }, [waypointLat, waypointLng, setValue, clearErrors]);

  const onSubmit = async (data: FormValues) => {
    const { name, waypoint_type, comments, waterbodyId } = data;
    const waypointParams = {
      comments,
      lat: data.lat,
      lng: data.lng,
      name,
      waterbody: waterbodyId || null,
      waypoint_type,
    };

    const isValid = await trigger(
      [
        formFields.lat,
        formFields.lng,
        formFields.name,
        formFields.lat_deg,
        formFields.lat_min,
        formFields.lat_sec,
        formFields.lng_deg,
        formFields.lng_min,
        formFields.lng_sec,
      ],
      { shouldFocus: true }
    );

    if (!isValid) {
      setIsSubmitting(false);
      return;
    }

    if (newWaypoint) {
      createWaypoint.mutate(
        {
          ...waypointParams,
          imgFile,
        },
        {
          onSuccess: (waypoint) => {
            onNewWaypointChange(null);
          },
          onSettled: () => {
            setIsSubmitting(false);
          },
        }
      );
    }

    if (editableWaypoint) {
      updateWaypoint.mutate(
        {
          id: editableWaypoint.id,
          waypointParams: {
            ...waypointParams,
            imgFile,
            remove_img: removeImg,
          },
        },
        {
          onSuccess: (waypoint) => {
            onSelectedWaypointChange(waypoint); // sets active waypoint in open modal
            onEditableWaypointChange(null); // removes draggable marker
          },
          onSettled: () => {
            setIsSubmitting(false);
          },
        }
      );
    }
  };

  const fetchWaterbody = useCallback(async (waterbodyId: string) => {
    if (!waterbodyId) {
      setInteractiveWaterbody(null);
      setValue(formFields.waterbodyId, null);
      return;
    }
    const res = await apiV1.waterbodyFetch(waterbodyId);
    if (res.data.data) {
      setInteractiveWaterbody(res.data.data);
      setValue(formFields.waterbodyId, res.data.data.id);
    }
  }, []);

  const firstRenderRef = React.useRef(true);
  useEffect(
    function getInitialWaterbody() {
      const isFirstRender = firstRenderRef.current;

      if (isFirstRender && waypointLat && waypointLng && !waypointWaterbodyId) {
        const waterbodyId: string | undefined = queryRenderedFeaturesAtPoint(
          mapRef,
          waypointLat,
          waypointLng
        );
        fetchWaterbody(waterbodyId);
      }
    },
    [mapRef, waypointLat, waypointLng]
  );

  useEffect(
    function getWaterbodyOnDrag() {
      if (firstRenderRef.current) {
        firstRenderRef.current = false;
        return;
      }
      debouncedGetWaterbodyIdAtPoint(mapRef, waypointLat, waypointLng, (waterbodyId) => {
        // Ensure waterbodyId is different from current before fetching
        if (waterbodyId === interactiveWaterbody?.id) {
          return;
        }
        fetchWaterbody(waterbodyId);
      });
    },
    [mapRef, waypointLat, waypointLng]
  );

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
        handleSubmit(onSubmit)();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleSubmit, onSubmit, editableWaypoint, newWaypoint]);

  const showDecimal = coordinateType === 'Decimal';
  const showDMS = coordinateType === 'DMS';

  const [imgFile, setImgFile] = useState<File | null>(null);
  const [previewImg, setPreviewImg] = useState('');
  const [removeImg, setRemoveImg] = useState(false);

  const dateTime = editableWaypoint ? dayjs(editableWaypoint.outing_at) : dayjs();

  return (
    <>
      <div className={styles.body}>
        <form className={styles.form}>
          <div className={styles.column}>
            <label htmlFor={formFields.name}>Waypoint Name</label>
            <input
              name={formFields.name}
              {...register(formFields.name, { required: true })}
              id={formFields.name}
              type="text"
              className={styles.input}
            />
            {errors.name && <span className={styles.error}>A name is required</span>}
          </div>
          <div className={styles.lakeSearch}>
            <label htmlFor="waterbody_select">Waterbody</label>
            <Controller
              control={control}
              name="waterbodyId"
              render={({ field }) => (
                <WaterbodySearchSelect
                  {...field}
                  id="waterbody_select"
                  allowClear
                  placeholder="Ray Roberts Lake"
                  showSearch
                  labelInValue
                  suffixIcon={<SearchOutlined />}
                  waterbodyLookupValueProp="id"
                  onChange={(val: WaterbodySelectValue) => {
                    if (val?.label?.props['data-result']) {
                      setInteractiveWaterbody(val.label.props['data-result']);
                      field.onChange(val.value);
                    } else {
                      setInteractiveWaterbody(null);
                      field.onChange(null);
                    }
                  }}
                  value={
                    interactiveWaterbody && {
                      label: createLabel(interactiveWaterbody),
                      value: interactiveWaterbody?.id,
                    }
                  }
                />
              )}
            />
          </div>
          <div className={styles.column__date}>
            <span className={styles.label}>Date/Time</span>
            <span>{`${dateTime.format('MMM D, YYYY')} at ${dateTime.format('h:mm A')}`}</span>
          </div>

          <div className={styles.coordinateToggle}>
            <Segmented
              options={['Decimal', 'DMS']}
              value={coordinateType}
              onChange={(val: 'Decimal' | 'DMS') => setCoordinateType(val)}
            />
          </div>
          {showDecimal && (
            <div className={styles.row}>
              <div className={styles.column__latLng}>
                <label htmlFor={formFields.lat}>Latitude</label>
                <span>
                  <input
                    id={formFields.lat}
                    type="number"
                    step="any"
                    {...register(formFields.lat, {
                      required: 'Valid latitude value is required.',
                      valueAsNumber: true,
                      onChange: (e) => handleLatChange(e),
                      validate: (value) => {
                        if (value > 90 || value < -90) {
                          return 'Latitude must be between -90 and 90 degrees.';
                        }
                      },
                    })}
                    className={styles.latLng}
                  />
                  <span className={styles.identifier}>°</span>
                </span>
                {errors.lat && <span className={styles.error}>{errors.lat.message}</span>}
              </div>
              <div className={styles.column__latLng}>
                <label htmlFor={formFields.lng}>Longitude</label>
                <span>
                  <input
                    id={formFields.lng}
                    type="number"
                    step="any"
                    {...register(formFields.lng, {
                      required: 'Valid longitude value is required.',
                      valueAsNumber: true,
                      onChange: (e) => handleLngChange(e),
                      validate: (value) => {
                        if (value > 180 || value < -180) {
                          return 'Longitude must be between -180 and 180 degrees.';
                        }
                      },
                    })}
                    className={styles.latLng}
                  />
                  <span className={styles.identifier}>°</span>
                </span>
                {errors.lng && <span className={styles.error}>{errors.lng.message}</span>}
              </div>
            </div>
          )}
          {showDMS && (
            <div className={styles.row}>
              <div className={styles.row__dms}>
                <div className={styles.column}>
                  <label htmlFor={formFields.lat_deg}>Latitude</label>
                  <div className={styles.row}>
                    <input
                      {...register(formFields.lat_deg, {
                        required: 'Valid latitude degrees required.',
                        valueAsNumber: true,
                        onChange: (e) => handleLatDMSChange(formFields.lat_deg, e.target.value),
                      })}
                      className={styles.input__small}
                      id={formFields.lat_deg}
                      type="number"
                    />
                    <span className={styles.identifier}>°</span>
                    <label htmlFor={formFields.lat_min} style={{ display: 'none' }}>
                      Minutes
                    </label>
                    <input
                      {...register(formFields.lat_min, {
                        required: 'Valid minutes value required.',
                        valueAsNumber: true,
                        onChange: (e) => handleLatDMSChange(formFields.lat_min, e.target.value),
                      })}
                      className={styles.input__small}
                      id={formFields.lat_min}
                      type="number"
                    />
                    <span className={styles.identifier}>'</span>
                    <label htmlFor={formFields.lat_sec} style={{ display: 'none' }}>
                      Seconds
                    </label>
                    <input
                      {...register(formFields.lat_sec, {
                        required: 'Valid seconds value required.',
                        valueAsNumber: true,
                        onChange: (e) => handleLatDMSChange(formFields.lat_sec, e.target.value),
                      })}
                      className={styles.input__medium}
                      id={formFields.lat_sec}
                      step="any"
                      type="number"
                    />
                    <span className={styles.identifier}>"</span>
                  </div>
                  {errors.lat_deg && <span className={styles.error}>{errors.lat_deg.message}</span>}
                </div>
              </div>
              <div className={styles.row__dms}>
                <div className={styles.column}>
                  <label htmlFor={formFields.lng_deg}>Longitude</label>
                  <div className={styles.row}>
                    <input
                      {...register(formFields.lng_deg, {
                        required: 'Valid longitude degrees required.',
                        valueAsNumber: true,
                        onChange: (e) => handleLngDMSChange(formFields.lng_deg, e.target.value),
                      })}
                      className={styles.input__small}
                      id={formFields.lng_deg}
                      step="any"
                      type="number"
                    />
                    <span className={styles.identifier}>°</span>
                    <label htmlFor={formFields.lng_min} style={{ display: 'none' }}>
                      Minutes
                    </label>
                    <input
                      {...register(formFields.lng_min, {
                        required: 'Valid minutes value required.',
                        valueAsNumber: true,
                        onChange: (e) => handleLngDMSChange(formFields.lng_min, e.target.value),
                      })}
                      className={styles.input__small}
                      id={formFields.lng_min}
                      step="any"
                      type="number"
                    />
                    <span className={styles.identifier}>'</span>
                    <label htmlFor={formFields.lng_sec} style={{ display: 'none' }}>
                      Seconds
                    </label>
                    <input
                      {...register(formFields.lng_sec, {
                        required: 'Valid seconds value required.',
                        valueAsNumber: true,
                        onChange: (e) => handleLngDMSChange(formFields.lng_sec, e.target.value),
                      })}
                      className={styles.input__medium}
                      id={formFields.lng_sec}
                      step="any"
                      type="number"
                    />
                    <span className={styles.identifier}>"</span>
                  </div>
                  {errors.lng_deg && <span className={styles.error}>{errors.lng_deg.message}</span>}
                </div>
              </div>
            </div>
          )}

          <div className={styles.column}>
            <fieldset>
              <legend className={styles.label__legend}>Waypoint Type</legend>
              <div className={styles.waypointTypeList}>
                {waypointTypes.map((wt) => (
                  <label key={wt} className={styles.waypointType}>
                    <input
                      type="radio"
                      value={wt}
                      {...register(formFields.waypoint_type, {
                        required: true,
                        onChange: (e) => {
                          const waypoint_type = e.target.value as Waypoint['waypoint_type'];
                          if (newWaypoint) {
                            onNewWaypointChange({ ...newWaypoint, waypoint_type });
                          }
                          if (editableWaypoint) {
                            onEditableWaypointChange({ ...editableWaypoint, waypoint_type });
                          }
                        },
                      })}
                    />{' '}
                    <span
                      className={classNames(styles.waypointMarker, {
                        [styles.waypointMarker__selected]: waypointType === wt,
                      })}
                    >
                      <WaypointMarker waypoint_type={wt} />
                    </span>
                    <span
                      className={classNames(styles.waypointTypeLabel, {
                        [styles.waypointTypeLabel__selected]: waypointType === wt,
                      })}
                    >
                      {getLabel(wt)}
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>
          </div>
          <div className="mt-3 ">
            {(previewImg || editableWaypoint?.img) && !removeImg && (
              <>
                <div className="mb-3 ">
                  <strong>Image</strong>
                  <span className="block max-w-[200px] max-h-[200px] w-fit relative">
                    <img
                      src={getImgixPath(previewImg || editableWaypoint.img, { w: 200 })}
                      alt=""
                      className="rounded-md max-h-[200px]"
                    />
                    <OmniaButton
                      kind="tertiary"
                      size="sm"
                      shape="circle"
                      onPress={() => {
                        setRemoveImg(true);
                        setImgFile(null);
                        setPreviewImg('');
                      }}
                      className="mb-3 absolute -top-3 -right-3 border border-gray-300 hover:border-gray-500"
                    >
                      x
                    </OmniaButton>
                  </span>
                </div>
                <div></div>
              </>
            )}
            {!previewImg && (
              <>
                <label htmlFor={'image'} className="mb-1.5 block">
                  Upload Image
                </label>
                <input
                  type="file"
                  id={'image'}
                  className={styles.input}
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (!file) {
                      return;
                    }
                    setImgFile(file);
                    setPreviewImg(URL.createObjectURL(file));
                    setRemoveImg(false);
                  }}
                  accept="image/jpeg, image/jpg, image/png"
                />
              </>
            )}
          </div>
          <div className={styles.notes}>
            <label htmlFor={formFields.comments}>Notes</label>
            <textarea
              name={formFields.comments}
              id={formFields.comments}
              {...register(formFields.comments)}
              rows={4}
              placeholder="Write a private note..."
            />
          </div>
        </form>
        {newWaypoint && (
          <section className={styles.conditions}>
            <h3 className={styles.conditionsHeading}>Conditions</h3>
            <CurrentConditionsTimestamp
              lat={newWaypoint.lat}
              lng={newWaypoint.lng}
              timestamp={nowTimestamp.current}
              isProBlocked
            />
            {!isPro && <ConditionsUpgrade />}
          </section>
        )}
      </div>
      <div className={styles.footerWrapper}>
        <div className={styles.footer}>
          <OmniaButton
            kind="primary"
            size="md"
            block
            onPress={() => {
              setIsSubmitting(true);
              handleSubmit(onSubmit)();
            }}
            isDisabled={
              !!errors.lng_deg || !!errors.lat_deg || !!errors.lat || !!errors.lng || isSubmitting
            }
          >
            Save
          </OmniaButton>
        </div>
      </div>
    </>
  );
};
